/*
 * pcard-swatch-fit.js
 *
 * Keeps the product-card colour swatch row on a SINGLE line at any width. As
 * the card narrows, whole swatches that no longer fit are hidden (never
 * clipped, never wrapped) and the trailing "+N" overflow count is recomputed to
 * match — including the swatches the Liquid snippet already folded into "+N".
 *
 * Robustness/perf notes:
 *  - Placement is decided from REAL measured positions (each swatch's right
 *    edge vs the row's available right edge), not assumptions about gap/stride,
 *    so it's correct regardless of swatch size, spacing, or zoom.
 *  - Per row, reads are batched after a single write pass to avoid layout
 *    thrashing; the whole sweep is coalesced into one animation frame.
 *  - Idempotent: re-running yields the same result, so repeated triggers never
 *    fight each other or "render twice".
 *  - Re-fits on every relevant signal: first paint, full load, fonts ready,
 *    resize, orientation change, bfcache restore (pageshow), the theme's
 *    `collection:rerendered` event, and a MutationObserver on the grid (catches
 *    filter / sort / nav-menu collection switches / infinite scroll). The
 *    observer is disconnected while we mutate so it can't loop.
 */
(function () {
  if (window.__pcardSwatchFitInit) return;
  window.__pcardSwatchFitInit = true;

  var SELECTOR = '.product-card__swatches';

  function moreLinkOf(c) {
    return c.querySelector(':scope > a.reversed-link, :scope > a.product-card__swatch-more');
  }

  function productUrl(c) {
    var sw = c.querySelector('.swatch-color[href]');
    if (sw) return sw.getAttribute('href').split('?')[0];
    var card = c.closest('.product-card');
    var t = card && card.querySelector('.product-card__title a[href]');
    return t ? t.getAttribute('href') : '#';
  }

  function removeEl(el) {
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  function fit(container) {
    var ul = container.querySelector('.swatches');
    if (!ul) return; // empty/placeholder row
    var items = Array.prototype.slice.call(ul.querySelectorAll('li'));
    if (!items.length) return;

    // Single line is enforced in CSS; reassert inline as a belt-and-suspenders.
    container.style.flexWrap = 'nowrap';
    ul.style.flexWrap = 'nowrap';

    // Swatches Liquid already folded into the first "+N" — stash once so we
    // never re-read our own rewritten pill text.
    if (container.dataset.baseMore === undefined) {
      var seed = moreLinkOf(container);
      var s = seed ? parseInt((seed.textContent.match(/\d+/) || ['0'])[0], 10) : 0;
      container.dataset.baseMore = isNaN(s) ? 0 : s;
    }
    var baseMore = parseInt(container.dataset.baseMore, 10) || 0;
    var total = items.length + baseMore;

    // --- WRITE: reveal every swatch, drop the pill for a clean measurement ---
    for (var i = 0; i < items.length; i++) items[i].style.display = '';
    var link = moreLinkOf(container);
    if (link) link.style.display = 'none';

    // --- READ (one layout flush) ---
    var clientW = container.clientWidth;
    if (!clientW) return; // not laid out yet — a later trigger handles it
    var contLeft = container.getBoundingClientRect().left;
    var availRight = contLeft + clientW;
    var rights = new Array(items.length);
    for (i = 0; i < items.length; i++) rights[i] = items[i].getBoundingClientRect().right;

    // Everything fits and nothing was pre-folded → show all, no pill.
    if (baseMore === 0 && rights[items.length - 1] <= availRight + 1) {
      removeEl(link);
      return;
    }

    // A "+N" pill is needed — ensure one exists, measure its width.
    var created = false;
    if (!link) {
      link = document.createElement('a');
      link.className = 'reversed-link product-card__swatch-more';
      link.setAttribute('href', productUrl(container));
      container.appendChild(link);
      created = true;
    }
    link.style.display = '';
    link.textContent = '+' + (total - items.length);
    var gap = parseFloat(getComputedStyle(container).columnGap) || 8;
    var pillW = link.getBoundingClientRect().width || 30;
    var limit = availRight - pillW - gap;

    // Hiding trailing items never shifts the leading ones, so the right edges
    // measured above stay valid — pick the most that fit before the pill.
    var visible = 0;
    for (i = 0; i < items.length; i++) {
      if (rights[i] <= limit + 1) visible = i + 1;
      else break;
    }
    if (visible < 1) visible = 1;

    // --- WRITE: apply visibility + final count ---
    for (i = 0; i < items.length; i++) items[i].style.display = i < visible ? '' : 'none';
    var hidden = total - visible;
    if (hidden > 0) {
      link.style.display = '';
      link.textContent = '+' + hidden;
    } else if (created) {
      removeEl(link);
    } else {
      link.style.display = 'none';
    }
  }

  function fitAll() {
    var list = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < list.length; i++) fit(list[i]);
  }

  // ---- one rAF-coalesced sweep for every trigger ----
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () { scheduled = false; run(); });
  }

  var ro = ('ResizeObserver' in window) ? new ResizeObserver(schedule) : null;
  var roSeen = ('WeakSet' in window) ? new WeakSet() : null;
  function observeRows() {
    if (!ro) return;
    var list = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < list.length; i++) {
      var c = list[i];
      if (roSeen) { if (roSeen.has(c)) continue; roSeen.add(c); }
      ro.observe(c);
    }
  }

  var mo = ('MutationObserver' in window) ? new MutationObserver(schedule) : null;
  var moSeen = [];
  function watchGrids() {
    if (!mo) return;
    var grids = [document.getElementById('ProductGridContainer'), document.querySelector('.collection')];
    for (var i = 0; i < grids.length; i++) {
      var g = grids[i];
      if (g && moSeen.indexOf(g) === -1) {
        moSeen.push(g);
        mo.observe(g, { childList: true, subtree: true });
      }
    }
  }

  function run() {
    if (mo) mo.disconnect(); // our own DOM writes must not retrigger the observer
    observeRows();
    fitAll();
    if (mo) { moSeen = []; watchGrids(); }
  }

  function start() {
    run();
    requestAnimationFrame(run);
    window.addEventListener('load', schedule);
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    window.addEventListener('pageshow', schedule); // back/forward bfcache restore
    document.addEventListener('collection:rerendered', schedule);
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(schedule);
    }
  }

  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();
